import { useCallback, useEffect, useState } from "react";

/**
 * Small per-browser preferences for the new menu, kept in localStorage:
 *   navVersion       "v1" (old sidebar) | "v2" (module rail + panel)
 *   navV2.pins       pinned page keys
 *   navV2.recent     recently opened page keys, newest first
 *   navV2.panel      "open" | "closed"  (desktop page panel)
 *   navV2.split      "1" | "0"         (Dealership as Vehicle Sales + After-sales)
 *
 * Every change fires one window event so the rail, panel, search and phone menu
 * stay in step without sharing a context.
 */

// Preview: the old menu stays the default until the new one is signed off.
// Open any page with ?nav=v2 to switch (and ?nav=v1 to switch back).
export const DEFAULT_NAV_VERSION = "v1";

const EVENT = "nav-v2-prefs";
const MAX_RECENT = 6;

const read = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const write = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage blocked - the preference just won't persist */
  }
  window.dispatchEvent(new Event(EVENT));
};

export const getNavVersion = () => {
  const v = read("navVersion", DEFAULT_NAV_VERSION);
  return v === "v2" ? "v2" : "v1";
};
export const setNavVersion = (v) => write("navVersion", v === "v2" ? "v2" : "v1");

/** Reads ?nav=v1|v2 from a search string and saves it. Returns true when it changed something. */
export function applyNavVersionFromSearch(search) {
  const wanted = new URLSearchParams(search || "").get("nav");
  if ((wanted === "v1" || wanted === "v2") && wanted !== getNavVersion()) {
    setNavVersion(wanted);
    return true;
  }
  return false;
}

const snapshot = () => ({
  version: getNavVersion(),
  pins: read("navV2.pins", []),
  recent: read("navV2.recent", []),
  panelOpen: read("navV2.panel", "open") !== "closed",
  splitDealership: read("navV2.split", "1") !== "0",
});

export function pushRecent(key) {
  if (!key) return;
  const recent = read("navV2.recent", []);
  if (recent[0] === key) return;
  write("navV2.recent", [key, ...recent.filter((k) => k !== key)].slice(0, MAX_RECENT));
}

export default function useNavPrefs() {
  const [prefs, setPrefs] = useState(snapshot);

  useEffect(() => {
    const sync = () => setPrefs(snapshot());
    window.addEventListener(EVENT, sync);
    // Another tab changed them.
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const togglePin = useCallback((key) => {
    const pins = read("navV2.pins", []);
    write("navV2.pins", pins.includes(key) ? pins.filter((k) => k !== key) : [...pins, key]);
  }, []);
  const setPanelOpen = useCallback((open) => write("navV2.panel", open ? "open" : "closed"), []);
  const setSplitDealership = useCallback((on) => write("navV2.split", on ? "1" : "0"), []);

  return { ...prefs, togglePin, setPanelOpen, setSplitDealership, setVersion: setNavVersion };
}

/** Opens the Ctrl+K search from anywhere (the navbar search button). */
export const SEARCH_EVENT = "nav-v2-search";
export const openNavSearch = () => window.dispatchEvent(new Event(SEARCH_EVENT));
